import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Paragraph } from '@rover/kibble/core';

import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';

type DogTrainingServiceFaqsProps = {
  htmlRef?: React.RefObject<HTMLElement>;
};

const DogTrainingServiceFaqs: React.FC<DogTrainingServiceFaqsProps> = ({ htmlRef }) => {
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-training-service-faqs"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Dog Training on Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`How do trainers get approved to list on Rover?`}>
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>To list on Rover, trainers must:</Trans>
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans>Pass a third-party background check</Trans>
              </ListItem>
              <ListItem>
                <Trans>
                  Share their certificate of education or certification from an approved program.
                </Trans>
              </ListItem>
              <ListItem>
                <Trans>Commit to rewards-based training</Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        <FrequentlyAskedQuestion question={t`What is rewards-based training?`}>
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="2x">
              <Trans>
                Rewards-based training encourages dogs to offer desired behaviors by reinforcing the
                behavior with something the dog likes, including treats, play, or toys.
                Rewards-based training prioritizes using the least intrusive intervention; it
                promotes thinking rather than punishing mistakes.
              </Trans>
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                When rewards are used, the likelihood the behavior is repeated increases. The
                positive connection to the reward results in making learning enjoyable, strengthens
                the human-animal bond and over time leads to consistent desired behaviors.
              </Trans>
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`Where do training sessions happen?`}>
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="2x">
              <Trans>
                Training can happen many places — your home, a park, a store, or anywhere else that
                you and your trainer decide is best for the training you are doing! Many trainers
                will recommend training in your home. This allows them to observe your dog in their
                natural environment so they can effectively assess and best help reach your training
                goals.
              </Trans>
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Trainers will often mention on their profile where they typically hold training
                sessions, but you can always contact a trainer to learn more!
              </Trans>
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={t`What if I decide the trainer I contact is not the right fit?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              No problem! When you initially contact a trainer, you will be prompted to select a
              proposed first session date — this is not final, and is confirmed only if both parties
              decide to move forward. We also recommend contacting multiple trainers to make sure
              you find a trainer that is the right fit for you and your pet.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion question={t`Can I contact multiple trainers?`}>
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="2x">
              <Trans>
                Of course! Having a trainer that is the right fit for both you and your pup is an
                important step to reaching your training goals. We recommend reaching out to any
                trainer that interests you. Your initial request is not final until both you and the
                trainer decide to you want move forward with booking.
              </Trans>
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Also, trainers may offer or require consultations. Make sure to ask trainers so you
                can make the best decision for you and your pet.
              </Trans>
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={t`My dog has advanced training needs. Can trainers on Rover help me?`}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="2x">
              <Trans>
                Many trainers on Rover indicate experience in a wide range of behaviors, from puppy
                training to more advanced behaviors and training types.
              </Trans>
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Review a trainer's profile to see if their experience aligns with your needs, and if
                you have questions, don't hesitate to contact them to ensure if they're the right
                trainer for you and your pet.
              </Trans>
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogTrainingServiceFaqs;
